import { useState } from "react";
import type { CheckboxProps } from "..";
import type { PickRequired } from "../../types";
import type { CheckboxLabelProps } from "../Checkbox.types";

/**
 * @see https://clayground.vercel.app/components/checkbox/checkbox-group#usecheckboxgroup
 */
export const useCheckboxGroup = (items: PickRequired<CheckboxProps, "value">[] = []) => {
  const [value, setValue] = useState(
    items.map((item) => (item.checked ? item.value : undefined)).filter(Boolean),
  );

  const getCheckboxProps = ({
    value: _value,
    ...props
  }: PickRequired<Omit<CheckboxLabelProps, "onChangeChecked" | "checked">, "value">): CheckboxLabelProps => {
    const onChangeChecked = (checked: boolean) => {
      if (checked) {
        setValue((oldCheckedValues) => [...oldCheckedValues, _value]);

        return;
      }

      setValue((oldCheckedValues) => oldCheckedValues.filter((oldValue) => oldValue !== _value));
    };

    const checked = value.includes(_value);

    return {
      ...props,
      onChangeChecked,
      checked,
    };
  };

  return {
    value,
    setValue,
    getCheckboxProps,
  };
};

/**
 * @see https://clayground.vercel.app/components/checkbox/checkbox-group#useindeterminatecheckboxgroup
 */
export const useIndeterminateCheckboxGroup = (items: PickRequired<CheckboxLabelProps, "value">[]) => {
  const { value, getCheckboxProps, setValue } = useCheckboxGroup(items);

  const isAllChecked = items
    .filter((item) => !item.disabled)
    .every(({ value: _value }) => (value ? value.includes(_value) : false));

  const toggleAllCheck = () => {
    if (value.length > 0) {
      setValue([]);
      return;
    }

    setValue(items.filter((item) => !item.disabled).map((item) => item.value));
  };

  const getIndeterminateCheckboxProps = (props: CheckboxLabelProps) => {
    return {
      isIndeterminate: !isAllChecked,
      checked: value.length > 0,
      ...props,
    };
  };

  return {
    value,
    getCheckboxProps,
    toggleAllCheck,
    isAllChecked,
    getIndeterminateCheckboxProps,
  };
};
